import { useState, useEffect } from "react";
import {
  checkEmailExists,
  sendEmailOtp,
  saveUsername,
  buildUserData,
  isUsernameTaken,
  isValidUsername,
  USERNAME_PENDING_PREFIX,
  getUserData,
} from "../utils/authUtils";
import styles from "../styles/Signup.module.css";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "./context/user/UserContext";
import { useContext } from "react";
import { showSuccess, showError, showWarning } from "../utils/toastUtils";
import { EULA_URL } from "../utils/legal";
import OtpVerification from "./OtpVerification";

const Signup = () => {
  const { setUserData, isLoading: contextLoading } = useContext(UserContext);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [step, setStep] = useState("form");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    const userData = getUserData();

    if (userData && !contextLoading) {
      if (userData.user.isAdmin) {
        navigate("/admin");
      } else if (userData.user.isManager) {
        navigate("/AddSighting");
      } else {
        navigate("/account");
      }
    }
  }, [contextLoading, navigate]);

  const finishSignup = (session, profile) => {
    const data = buildUserData(session, profile);
    setUserData(data);

    showSuccess(`Account created successfully! Welcome, ${name}!`);
    if (data.user.isAdmin) {
      navigate("/admin");
    } else if (data.user.isManager) {
      navigate("/AddSighting");
    } else {
      navigate("/account");
    }
  };

  const validateInputs = () => {
    const trimmedName = name.trim().toLowerCase();

    if (!trimmedName) {
      setError(true);
      setErrorMessage("Please choose a username");
      showWarning("Please choose a username");
      return false;
    }

    if (!isValidUsername(trimmedName)) {
      setError(true);
      setErrorMessage(
        "Username must be 3-20 characters and contain only letters, numbers, or underscores",
      );
      showWarning(
        "Username must be 3-20 characters and contain only letters, numbers, or underscores",
      );
      return false;
    }

    if (trimmedName.startsWith(USERNAME_PENDING_PREFIX)) {
      setError(true);
      setErrorMessage("This username prefix is reserved. Please choose another");
      showWarning("This username prefix is reserved. Please choose another");
      return false;
    }

    if (!email) {
      setError(true);
      setErrorMessage("Email is required");
      showWarning("Email is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(true);
      setErrorMessage("Please enter a valid email address");
      showWarning("Please enter a valid email address");
      return false;
    }

    if (!acceptedTerms) {
      setError(true);
      setErrorMessage(
        "You must accept the Terms of Service, Privacy Policy, and EULA",
      );
      showWarning(
        "You must accept the Terms of Service, Privacy Policy, and EULA",
      );
      return false;
    }

    return true;
  };

  const handleSendCode = async () => {
    if (!validateInputs()) return;

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim().toLowerCase();

    setLoading(true);
    setError(false);

    try {
      // No passwords: we sign up with an email OTP, then save the username.
      const usernameTaken = await isUsernameTaken(trimmedName);
      if (usernameTaken) {
        setError(true);
        setErrorMessage("Username is already taken");
        showWarning("Username is already taken");
        return;
      }

      const emailExists = await checkEmailExists(trimmedEmail);
      if (emailExists) {
        setError(true);
        setErrorMessage(
          "An account already exists with this email. Please log in instead.",
        );
        showWarning(
          "An account already exists with this email. Please log in instead.",
        );
        return;
      }

      await sendEmailOtp(trimmedEmail, { shouldCreateUser: true });
      setStep("otp");
      showSuccess("Code sent! Check your email to finish signing up.");
    } catch (err) {
      setError(true);
      setErrorMessage(err.message || "Registration failed. Please try again.");
      showError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = async (data) => {
    const session = data?.session;
    if (!session?.user) {
      showError("Could not create your account. Please request a new code.");
      return;
    }

    try {
      const profile = await saveUsername(session, name);
      finishSignup(session, profile);
    } catch (err) {
      showError(err.message || "Could not save your username. Please try again.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendCode();
    }
  };

  if (step === "otp") {
    return (
      <OtpVerification
        email={email.trim().toLowerCase()}
        shouldCreateUser
        onVerified={handleVerified}
        onBack={() => setStep("form")}
        title="Verify Your Email"
        subtitle="We sent a 6-digit code to"
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h1>Sign Up</h1>
        <p className={styles.instruction}>
          Choose a username and enter your email. We'll send you a 6-digit code
          to verify it — no password needed.
        </p>
        <input
          placeholder="Username"
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus
        />
        <input
          placeholder="Email"
          className={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <div className={styles.termsContainer}>
          <input
            type="checkbox"
            id="terms"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className={styles.checkbox}
          />
          <label htmlFor="terms" className={styles.termsText}>
            I AGREE to the{" "}
            <Link to="/terms#terms-of-service" target="_blank">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/terms#privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            , and the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
              Apple's Standard End User License Agreement (EULA)
            </a>
          </label>
        </div>
        <button
          onClick={handleSendCode}
          className={styles.button}
          disabled={loading}
        >
          {loading ? "Sending..." : "Send Code"}
        </button>
        {error && <span className={styles.error}>{errorMessage}</span>}
        <div className={styles.linkContainer}>
          <p className={styles.switchLink}>
            Already have an account? <Link to="/login">Log In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
